import { pb, escapeFilterValue } from '../lib/pocketbase'
import type { PlayerTransaction, PlayerTransactionPayload, PlayerTransactionType } from '../types'
import type { ESPNTransaction } from './espnData'

function mapPlayerTransaction(record: Record<string, any>): PlayerTransaction {
  return {
    id: record.id,
    player_name: record.player_name ?? '',
    transaction_type: record.transaction_type ?? 'signed',
    from_team: record.from_team ?? null,
    to_team: record.to_team ?? null,
    transaction_date: record.transaction_date ?? '',
    contract_total: record.contract_total ?? null,
    contract_apy: record.contract_apy ?? null,
    contract_years: record.contract_years ?? null,
    guaranteed_money: record.guaranteed_money ?? null,
    position: record.position ?? null,
    league: record.league ?? null,
    source: record.source ?? null,
    source_url: record.source_url ?? null,
    is_agency_client: record.is_agency_client ?? false,
    client_id: record.client_id ?? null,
    notes: record.notes ?? null,
    agency_id: record.agency_id ?? '',
    created_by: record.created_by ?? null,
    created: record.created ?? '',
    updated: record.updated ?? '',
  }
}

function getAgencyId(): string {
  const user = pb.authStore.model
  const agencyId = (user as any)?.agency_id
  if (!agencyId) throw new Error('No agency_id found on current user')
  return agencyId
}

export type PlayerTransactionFilters = {
  type?: PlayerTransactionType
  team?: string
  league?: string
  dateFrom?: string
  dateTo?: string
  agencyClientsOnly?: boolean
  search?: string
}

export async function listPlayerTransactions(
  filters?: PlayerTransactionFilters,
): Promise<PlayerTransaction[]> {
  const agencyId = getAgencyId()

  const clauses: string[] = [
    `agency_id = "${escapeFilterValue(agencyId)}"`,
  ]

  if (filters?.type) {
    clauses.push(`transaction_type = "${escapeFilterValue(filters.type)}"`)
  }
  if (filters?.team) {
    const teamEsc = escapeFilterValue(filters.team)
    clauses.push(`(from_team ~ "${teamEsc}" || to_team ~ "${teamEsc}")`)
  }
  if (filters?.league) {
    clauses.push(`league = "${escapeFilterValue(filters.league)}"`)
  }
  if (filters?.dateFrom) {
    clauses.push(`transaction_date >= "${escapeFilterValue(filters.dateFrom)}"`)
  }
  if (filters?.dateTo) {
    clauses.push(`transaction_date <= "${escapeFilterValue(filters.dateTo)}"`)
  }
  if (filters?.agencyClientsOnly) {
    clauses.push('is_agency_client = true')
  }
  if (filters?.search) {
    clauses.push(`player_name ~ "${escapeFilterValue(filters.search)}"`)
  }

  const data = await pb.collection('player_transactions').getFullList<Record<string, any>>({
    filter: clauses.join(' && '),
    sort: '-transaction_date',
  })
  return data.map(mapPlayerTransaction)
}

export async function getPlayerTransaction(id: string): Promise<PlayerTransaction> {
  const agencyId = getAgencyId()
  const record = await pb.collection('player_transactions').getOne<Record<string, any>>(id)
  if (record.agency_id !== agencyId) throw new Error('Not authorized')
  return mapPlayerTransaction(record)
}

export async function createPlayerTransaction(
  payload: PlayerTransactionPayload,
): Promise<PlayerTransaction> {
  const agencyId = getAgencyId()
  const user = pb.authStore.model

  const record = await pb.collection('player_transactions').create({
    ...payload,
    agency_id: agencyId,
    created_by: user?.id ?? null,
  })
  return mapPlayerTransaction(record)
}

export async function updatePlayerTransaction(
  id: string,
  payload: Partial<PlayerTransactionPayload>,
): Promise<PlayerTransaction> {
  const agencyId = getAgencyId()
  const existing = await pb.collection('player_transactions').getOne<Record<string, any>>(id)
  if (existing.agency_id !== agencyId) throw new Error('Not authorized')

  const record = await pb.collection('player_transactions').update(id, payload)
  return mapPlayerTransaction(record)
}

export async function deletePlayerTransaction(id: string): Promise<void> {
  const agencyId = getAgencyId()
  const existing = await pb.collection('player_transactions').getOne<Record<string, any>>(id)
  if (existing.agency_id !== agencyId) throw new Error('Not authorized')

  await pb.collection('player_transactions').delete(id)
}

export async function listClientPlayerTransactions(
  clientId: string,
): Promise<PlayerTransaction[]> {
  const agencyId = getAgencyId()

  const filter = [
    `client_id = "${escapeFilterValue(clientId)}"`,
    `agency_id = "${escapeFilterValue(agencyId)}"`,
  ].join(' && ')

  const data = await pb.collection('player_transactions').getFullList<Record<string, any>>({
    filter,
    sort: '-transaction_date',
  })
  return data.map(mapPlayerTransaction)
}

/**
 * Map an ESPN transaction type string to our PlayerTransactionType.
 * ESPN types that don't have a direct match map to 'signed' as the closest default.
 */
function mapESPNTransactionType(espnType: string): PlayerTransactionType {
  const mapping: Record<string, PlayerTransactionType> = {
    'signed': 'signed',
    're-signed': 'signed',
    'released': 'released',
    'traded': 'traded',
    'placed_ir': 'waived',
    'activated': 'reinstated',
    'franchise_tagged': 'franchise_tagged',
    'waived': 'waived',
    'retired': 'retired',
    'nil_deal': 'nil_deal',
    'transfer_portal_entry': 'transfer_portal_entry',
    'transfer_portal_commit': 'transfer_portal_commit',
    'scholarship': 'scholarship',
  }
  return mapping[espnType] ?? 'signed'
}

/**
 * Import an ESPN transaction into PocketBase.
 * The caller should provide the clientId if this transaction matches an agency client.
 */
export async function importESPNTransaction(
  espnTx: ESPNTransaction,
  clientId?: string | null,
): Promise<PlayerTransaction> {
  const payload: PlayerTransactionPayload = {
    player_name: espnTx.playerName ?? espnTx.team,
    transaction_type: mapESPNTransactionType(espnTx.transactionType),
    transaction_date: espnTx.date,
    to_team: espnTx.team,
    position: espnTx.position,
    source: 'espn',
    is_agency_client: !!clientId,
    client_id: clientId ?? null,
    notes: espnTx.rawDescription,
  }

  return createPlayerTransaction(payload)
}
